// Native HTML5 drag-out for stored files.
//
// Wiring a file row with these props lets the user DRAG it straight out of the
// browser onto Outlook (a new email), the Windows desktop, or Explorer — the
// browser downloads the file at the drop target. This is the standard
// `DownloadURL` data-transfer type, supported by Chromium (Chrome / Edge).
//
// It needs a token-bearing URL the OS can fetch WITHOUT the app's auth header
// (the drag download is a plain browser GET, not an app fetch). The server
// mints a per-file `dl_token` on the listing row; see services/downloadToken.js
// and routes/download.js.
//
// Scope note: DownloadURL drops a real file onto the OS / native apps (Outlook
// desktop, Explorer, the Claude/ChatGPT *desktop* apps). Dropping into a
// browser TAB (e.g. claude.ai in another tab) reads dataTransfer.files, which
// a cross-app DownloadURL drag does not populate — use the Download button and
// attach for those.

// Absolute URL the OS download will hit (relative won't resolve for the OS).
export const dlUrl = (file) =>
  file && file.dl_token ? `${window.location.origin}/api/dl/${file.dl_token}` : null;

// onDragStart handler. Returns early (drag is a no-op) when no token exists.
export function fileDragStart(e, file) {
  const url = dlUrl(file);
  if (!url) return;
  const mime = file.mime_type || 'application/octet-stream';
  // DownloadURL format is "mime:filename:url". The filename can't contain a
  // colon or newline; the URL keeps its own colons (consumer takes everything
  // after the 2nd colon as the URL).
  const name = (file.original_name || 'file').replace(/[:\r\n]/g, '_');
  try {
    e.dataTransfer.setData('DownloadURL', `${mime}:${name}:${url}`);
    e.dataTransfer.setData('text/uri-list', url);
    e.dataTransfer.setData('text/plain', url);
    e.dataTransfer.effectAllowed = 'copy';
  } catch (_) { /* older browsers may block some types — DownloadURL is the one that matters */ }
}

// Spread onto a file row to make it draggable. `draggable` is false (so no
// ghost drag) when the file has no token.
export const fileDragProps = (file) => ({
  draggable: !!(file && file.dl_token),
  onDragStart: (e) => fileDragStart(e, file),
  title: file && file.dl_token ? 'Drag to Outlook or your desktop to attach this file' : undefined,
});
